import { Component, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { Popover, PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';
import { MenuItem } from 'primeng/api';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { BulkUploadComponent, BulkUploadTab } from '@ui-nuvertos/shared';
import { PageActionService } from '@ui-nuvertos/shared';
import { PageAction, PageActionEvent } from '@ui-nuvertos/shared';
import { MastersService } from '@ui-nuvertos/shared';
import { AuthService } from '@ui-nuvertos/auth';

@Component({
    selector: 'ui-header',
    standalone: true,
    imports: [CommonModule, BreadcrumbModule, ButtonModule, DialogModule, PopoverModule, TooltipModule, BulkUploadComponent],
    templateUrl: './header.html',
    styleUrls: ['./header.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
    @ViewChild('actionsPopover') actionsPopover!: Popover;

    breadcrumbItems: MenuItem[] = [];
    actionItems: {
        showFavorite?: boolean;
        showBulkUpload?: boolean;
        showBack?: boolean;
        backRoute?: string;
        redirectButtons?: Array<{ label: string; route: string; roles?: string[] }>;
        actionButtons?: Array<{ label: string; action: string; icon?: string }>;
    } | null = null;
    private routerSubscription?: Subscription;
    private pageActionSubscription?: Subscription;
    
    showBulkUploadDialog: boolean = false;
    currentModule: string = '';
    formTemplateId?: string | number;
    bulkUploadTabs: BulkUploadTab[] = [];
    
    /** Page actions for the popover menu */
    pageActions: PageAction[] = [];

    /** Get actions to show in toolbar as standalone buttons */
    get toolbarActions(): PageAction[] {
        return this.pageActions.filter(a => a.showInToolbar === true);
    }

    /** Get actions to show in dropdown menu */
    get dropdownActions(): PageAction[] {
        return this.pageActions.filter(a => a.showInToolbar !== true);
    }

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private pageActionService: PageActionService,
        private mastersService: MastersService,
        private authService: AuthService
    ) { }

    /** Check if a redirect button should be visible based on roles */
    isButtonVisible(button: { label: string; route: string; roles?: string[] }): boolean {
        if (!button.roles || button.roles.length === 0) return true;
        const currentRole = this.authService.getCurrentUserRole();
        console.log('currentRole', currentRole)
        return currentRole ? button.roles.includes(currentRole) : false;
    }

    ngOnInit() {
        // Subscribe to dynamic action state updates
        this.pageActionSubscription = this.pageActionService.actions.subscribe(
            actions => this.pageActions = actions
        );
        
        this.buildBreadcrumbs();
        this.buildActions();

        this.routerSubscription = this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                map(() => this.activatedRoute)
            )
            .subscribe(() => {
                this.buildBreadcrumbs();
                this.buildActions();
            });
    }

    ngOnDestroy() {
        this.routerSubscription?.unsubscribe();
        this.pageActionSubscription?.unsubscribe();
    }

    private buildBreadcrumbs() {
        const breadcrumbs: MenuItem[] = [];
        let route = this.activatedRoute.root;

        while (route.firstChild) {
            route = route.firstChild;
            const breadcrumb = route.snapshot.data['breadcrumb'];
            
            if (breadcrumb) {
                breadcrumbs.push({ label: breadcrumb });
            }
        }

        this.breadcrumbItems = breadcrumbs;
    }

    private buildActions() {
        let route = this.activatedRoute.root;

        while (route.firstChild) {
            route = route.firstChild;
        }

        const actions = route.snapshot.data['actions'];
        const routeData = route.snapshot.data;
        const urlPath = this.router.url;
        this.currentModule = this.extractModuleFromRoute(urlPath);

        // Extract form_template_id from route data if available
        if (routeData['form_template_id']) {
            this.formTemplateId = routeData['form_template_id'];
        }

        // Set tabs for vendor module (vendor + employee upload)
        if (this.currentModule === 'vendor') {
            this.bulkUploadTabs = [
                { label: 'Vendor', module: 'vendor', mode: 'create' },
                { label: 'Employee', module: 'employee', mode: 'create' },
            ];
        } else {
            this.bulkUploadTabs = [];
        }
        
        const showFavorite = actions?.showFavorite === false ? false : true;
        
        if (actions) {
            this.actionItems = {
                ...actions,
                showFavorite: showFavorite
            };
        } else {
            this.actionItems = {
                showFavorite: true
            };
        }
        // Note: pageActions are set dynamically by components via PageActionService
    }

    private extractModuleFromRoute(urlPath: string): string {
        // Extract module name from URL path (e.g., '/vendors' -> 'vendor')
        const segments = urlPath.split('/').filter(s => s);
        if (segments.length > 0) {
            const firstSegment = segments[0].toLowerCase();
            const secondSegment = segments.length > 1 ? segments[1].toLowerCase() : '';
            const fullPath = urlPath.toLowerCase();
            
            // Handle vendor sub-routes
            if (firstSegment === 'vendors') {
                if (secondSegment === 'vendor-payment-list') return 'vendor-payment';
                if (secondSegment === 'purchase-order' || fullPath.includes('purchase-order')) return 'vendor-po';
                if (secondSegment === 'purchase-invoice' || fullPath.includes('purchase-invoice')) return 'vendor-invoice';
                if (secondSegment === 'grn' || fullPath.includes('/grn')) return 'grn';
                if (secondSegment === 'return' || fullPath.includes('/return')) return 'vendor-return';
                if (secondSegment === 'debit-note' || fullPath.includes('debit-note')) return 'vendor-debit-note';
                return 'vendor';
            }
            
            // Handle client sub-routes
            if (firstSegment === 'clients') {
                if (secondSegment === 'client-payment-list') return 'client-payment';
                if (secondSegment === 'sales-order' || fullPath.includes('sales-order')) return 'client-order';
                if (secondSegment === 'client-invoice' || fullPath.includes('client-invoice')) return 'client-invoice';
                if (secondSegment === 'return' || fullPath.includes('/return')) return 'client-return';
                if (secondSegment === 'credit-note' || fullPath.includes('credit-note')) return 'credit-note';
                if (secondSegment === 'refund-payment' || fullPath.includes('refund')) return 'client-refund';
                return 'client';
            }
            
            // Handle journal voucher routes
            if (firstSegment === 'finance' && (secondSegment === 'journal-vouchers' || fullPath.includes('journal-voucher'))) {
                return 'journal-voucher';
            }
            
            // Handle BMW masters sub-routes (URL is /settings/masters/containers in the BMW app)
            if (fullPath.includes('/masters/containers')) return 'bmw-container';

            // Map common route segments to module names
            if (firstSegment.includes('vendor')) return 'vendor';
            if (firstSegment.includes('client')) return 'client';
            if (firstSegment.includes('item')) return 'item';
            if (firstSegment.includes('inventory')) return 'inventory';
            
            return firstSegment;
        }
        return '';
    }

    openBulkUpload() {
        this.showBulkUploadDialog = true;
    }

    onBulkUploadSuccess(response: any) {
        // Handle successful upload - job has been created and is processing in background
        console.log('Upload job created for module:', this.currentModule, response);
        // Toast is already shown by BulkUploadComponent - no reload needed
        // User can navigate to /uploads to track progress
    }

    onBulkUploadError(error: any) {
        // Error is already handled by the component, but we can add additional logic here
        console.error('File upload error for module:', this.currentModule, error);
    }

    goBack() {
        if (this.actionItems?.backRoute) {
            this.router.navigate([this.withRemotePrefix(this.actionItems.backRoute)]);
        } else {
            window.history.back();
        }
    }

    navigateTo(route: string) {
        this.router.navigate([this.withRemotePrefix(route)]);
    }

    /** When the host has mounted us under a federated remote (e.g. `/cssd`),
     *  the route registers `data.remoteBase`. Prepend that base to absolute
     *  paths declared by the remote's own route data so they resolve under
     *  the mount point instead of falling through to the shell wildcard. */
    private withRemotePrefix(route: string): string {
        if (!route.startsWith('/')) return route;
        let r = this.activatedRoute.root;
        while (r) {
            const base = r.snapshot.data?.['remoteBase'];
            if (base && !route.startsWith(`/${base}/`) && route !== `/${base}`) {
                return `/${base}${route}`;
            }
            if (!r.firstChild) break;
            r = r.firstChild;
        }
        return route;
    }

    /** Execute action from action buttons */
    executeAction(action: string): void {
        switch (action) {
            case 'exportMasters':
                this.mastersService.exportAllMasters();
                break;
            default:
                // Dispatch to PageActionService so page components can handle it
                this.pageActionService.dispatch({
                    action: { id: action, label: action },
                    routePath: this.router.url,
                });
                break;
        }
    }

    /** Handle page action click from popover */
    onPageActionClick(action: PageAction): void {
        // Close popover immediately
        this.actionsPopover?.hide();
        
        if (action.disabled) return;
        
        const event: PageActionEvent = {
            action,
            routePath: this.router.url,
        };
        this.pageActionService.dispatch(event);
    }
}
